import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  MessageCircleMore,
  Target,
  BrainCircuit,
  ClipboardList,
  Megaphone, Radio,
  BarChart3,
  Sparkles,
  Activity,
  Zap,
  Globe,
  Settings,
  Users,
  Clapperboard,
  ShieldCheck, ChevronRight, Menu,
} from "lucide-react";
import { NadiaAvatar } from "../nadia/NadiaAvatar";
import { useAuth } from "@/lib/auth";

const NAV_ITEMS = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/app/nadia", label: "Nadia AI", icon: MessageCircleMore, id: "nadia" },
  { to: "/app/obiettivo", label: "Obiettivo", icon: Target, id: "obiettivo" },
  { to: "/app/business-brain", label: "Business Brain", icon: BrainCircuit, id: "business-brain" },
  { to: "/app/piano", label: "Piano Marketing", icon: ClipboardList, id: "piano" },
  { to: "/app/campagne", label: "Campagne", icon: Megaphone, id: "campagne" },
  { to: "/app/ads", label: "Canali Ads", icon: Radio, id: "ads" },
  { to: "/app/uscite-social", label: "Uscite social", icon: Megaphone, id: "post-organici" },
  { to: "/app/video", label: "Video / Reel Studio", icon: Clapperboard, id: "video" },
  { to: "/app/landing", label: "Landing", icon: Globe, id: "landing" },
  { to: "/app/lead", label: "Lead", icon: Users, id: "lead" },
  { to: "/app/retargeting", label: "Retargeting", icon: Target, id: "retargeting" },
  { to: "/app/risultati", label: "Risultati", icon: BarChart3, id: "risultati" },
  { to: "/app/azioni", label: "Azioni Nadia", icon: Sparkles, id: "azioni" },
  { to: "/app/automazioni", label: "Automazioni", icon: Zap, id: "automazioni" },
  { to: "/app/attivita", label: "Attività Agente", icon: Activity, id: "attivita" },
  { to: "/app/impostazioni", label: "Impostazioni", icon: Settings, id: "impostazioni" },
];

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const NAV_GROUPS = [
  { label: "La mia attività", ids: ["dashboard", "obiettivo", "business-brain", "piano"], icon: LayoutDashboard },
  { label: "Marketing", ids: ["campagne", "ads", "post-organici", "video", "landing", "lead", "retargeting"], icon: Megaphone },
  { label: "Controllo", ids: ["risultati", "azioni", "automazioni", "attivita"], icon: Activity },
];

function MenuLink({ item: n, prefix, onNavigate }) {
  return <NavLink to={n.to} end={n.end} onClick={onNavigate} data-testid={prefix + "-" + n.id}
    className={({ isActive }) => "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors border " + (isActive ? "bg-cyan-500/10 text-cyan-200 border-cyan-500/20" : "text-slate-400 hover:text-slate-100 hover:bg-white/[0.03] border-transparent")}>
    <n.icon size={17} strokeWidth={1.75} /><span>{n.label}</span>
  </NavLink>;
}

function MenuGroup({ group, prefix, onNavigate }) {
  const { pathname } = useLocation();
  const items = NAV_ITEMS.filter((item) => group.ids.includes(item.id));
  const active = items.some((item) => item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(item.to + "/"));
  const [open, setOpen] = useState(active);
  useEffect(() => { if (active) setOpen(true); }, [pathname, active]);
  const id = prefix + "-group-" + group.ids[0];
  return <div className="mt-2">
    <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-controls={id}
      className={"w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-colors hover:bg-white/[0.03] " + (active ? "text-slate-100" : "text-slate-400")}>
      <group.icon size={17} strokeWidth={1.75} /><span className="flex-1 text-left">{group.label}</span>
      <ChevronRight size={15} className={"transition-transform " + (open ? "rotate-90" : "")} />
    </button>
    {open && <div id={id} className="ml-4 pl-2 border-l border-white/10 space-y-0.5 py-1">
      {items.map((item) => <MenuLink key={item.id} item={item} prefix={prefix} onNavigate={onNavigate} />)}
    </div>}
  </div>;
}

function NavigationMenu({ prefix = "nav", onNavigate }) {
  const { user } = useAuth();
  return <>
    <MenuLink item={NAV_ITEMS.find((item) => item.id === "nadia")} prefix={prefix} onNavigate={onNavigate} />
    {NAV_GROUPS.map((group) => <MenuGroup key={group.label} group={group} prefix={prefix} onNavigate={onNavigate} />)}
    <div className="mt-5 pt-3 border-t border-white/5">
      <MenuLink item={NAV_ITEMS.find((item) => item.id === "impostazioni")} prefix={prefix} onNavigate={onNavigate} />
      {user?.is_admin && <MenuLink item={{ to: "/app/amministrazione", label: "Amministrazione", icon: ShieldCheck, id: "amministrazione" }} prefix={prefix} onNavigate={onNavigate} />}
    </div>
  </>;
}

export function Sidebar({ compact = false }) {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  if (compact) return <aside className="nadia-rail" data-testid="sidebar-nav">
    <NavLink to="/app/nadia" className="nadia-rail-logo" aria-label="Nadia Cloud">N</NavLink>
    <nav aria-label="Navigazione principale" className="nadia-rail-links">
      <button type="button" className="nadia-rail-link" aria-expanded={expanded} aria-controls="nadia-all-functions" onClick={()=>setExpanded(v=>!v)}><Menu size={21}/><span>Tutte le funzioni</span></button>
    </nav>
    <span className="nadia-rail-account" title={user?.name||"Account"}>{(user?.name||"N").split(" ").map(n=>n[0]).slice(0,2).join("")}</span>
    {expanded&&<div className="nadia-rail-expanded" id="nadia-all-functions"><div className="flex items-center justify-between mb-4"><strong>Tutte le funzioni</strong><button type="button" aria-label="Chiudi menu" onClick={()=>setExpanded(false)}>×</button></div><NavigationMenu prefix="expanded-nav" onNavigate={()=>setExpanded(false)}/></div>}
  </aside>;
  return <aside className="hidden lg:flex fixed left-0 top-0 h-screen w-64 flex-col border-r border-white/5 bg-[#0B1120]/90 backdrop-blur-xl z-30" data-testid="sidebar-nav">
    <div className="px-5 py-6 flex items-center gap-3 border-b border-white/5">
      <NadiaAvatar size="md" />
      <div><div className="font-heading text-sm font-bold text-slate-50 leading-tight">Nadia Cloud</div><div className="text-[11px] text-slate-400">Direttore Marketing AI</div></div>
    </div>
    <nav aria-label="Navigazione principale" className="flex-1 px-3 py-5 overflow-y-auto"><NavigationMenu /></nav>
    <div className="p-4 text-xs text-slate-400 border-t border-white/5 truncate">{user?.name || "Nadia Cloud"}</div>
  </aside>;
}

export function BottomNav() {
  const [open, setOpen] = useState(false);
  const items = [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
    { to: "/app/nadia", label: "Nadia", icon: MessageCircleMore, id: "nadia" },
    { to: "/app/azioni", label: "Azioni", icon: Sparkles, id: "azioni" },
    { to: "/app/automazioni", label: "Auto", icon: Zap, id: "automazioni" },
    { to: "/app/impostazioni", label: "Impostazioni", icon: Settings, id: "impostazioni" },
  ];
  return <>
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-white/10 bg-[#0B1120]/95 backdrop-blur-xl px-1 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]" data-testid="bottom-nav" aria-label="Collegamenti rapidi">
      <div className="grid grid-cols-6 gap-0.5">
        {items.map((n) => <NavLink key={n.id} to={n.to} end={n.end} data-testid={"bottom-nav-" + n.id}
          className={({ isActive }) => "min-w-0 flex flex-col items-center gap-1 px-0.5 py-1.5 rounded-lg text-[9px] font-medium " + (isActive ? "text-cyan-300" : "text-slate-400")}>
          <n.icon size={19} strokeWidth={1.75} /><span>{n.label}</span>
        </NavLink>)}
        <button type="button" onClick={() => setOpen(true)} className="min-w-0 flex flex-col items-center gap-1 px-0.5 py-1.5 rounded-lg text-[9px] text-slate-300" data-testid="mobile-menu-open" aria-label="Apri tutte le funzioni"><Menu size={19} /><span>Menu</span></button>
      </div>
    </nav>
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="left" className="w-80 max-w-[90vw] bg-[#0B1120] border-white/10 text-slate-100 overflow-y-auto">
        <SheetHeader className="pb-5 border-b border-white/5"><SheetTitle className="flex items-center gap-3 text-slate-100"><NadiaAvatar size="sm" />Nadia Cloud</SheetTitle><SheetDescription>Direttore Marketing AI · tutte le funzioni</SheetDescription></SheetHeader>
        <nav className="mt-5" aria-label="Tutte le funzioni"><NavigationMenu prefix="mobile-nav" onNavigate={() => setOpen(false)} /></nav>
      </SheetContent>
    </Sheet>
  </>;
}
